import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, BookOpen, Activity } from 'lucide-react';

const HerniatedDiscCondition = () => {
  const [openFaqId, setOpenFaqId] = useState(null);

  const toggleFaq = (id) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  const pageData = {
    title: "Herniated Disc Treatment in New York City",
    metaDescription: "Expert care for herniated discs in New York. Discover personalized treatments to relieve pain and restore mobility.",
    metaKeywords: "Herniated disc treatment NYC, slipped disc, bulging disc, Dr. Tindel, spine pain relief",
    heroDescription: "Comprehensive solutions for herniated discs with personalized treatment plans to reduce pain and improve mobility.",
    forWho: [
      "A herniated disc, sometimes called a slipped or ruptured disc, happens when the soft inner gel of a spinal disc pushes out through a crack in the tougher outer layer. This protrusion can irritate or compress nearby spinal nerves, causing pain, numbness, or weakness in the area served by those nerves—commonly the arms (for cervical discs) or legs (for lumbar discs).",
      "Acute trauma or heavy lifting can lead to a disc herniation, but everyday wear and tear also weakens discs over time. Symptoms can range from mild to debilitating, and the location of the herniated disc often determines the type of pain you feel.."
    ],
    conditionInfo: [
      "If you’re experiencing shooting pain down an arm or leg, or if routine movements suddenly become excruciating, a herniated disc might be responsible. Our team at the NY Center for Spinal Disorders in Manhattan can diagnose the issue through imaging and discuss whether surgical or other interventions are appropriate for your situation.",
    ],
    treatment: [
      "Treatment may include physical therapy, anti-inflammatory medications, or targeted injections.",
      "Minimally invasive procedures such as microdiscectomy can relieve nerve pressure if conservative treatments fail.",
      "Our approach prioritizes pain relief and restoring function while minimizing the need for surgery."
    ],
    postCare: [
      "We provide structured rehabilitation programs to strengthen your spine and prevent recurrence.",
      "Lifestyle modifications, including posture correction and core strengthening exercises, support long-term recovery."
    ],
    nonSurgicalOptions: [
      "Physical therapy & targeted exercises",
      "Epidural steroid injections",
      "Chiropractic care & acupuncture"
    ],
    faqs: [
      {
        id: 1,
        question: "Can a herniated disc heal on its own?",
        answer: "In many cases, symptoms improve with conservative treatments like rest, physical therapy, and pain management."
      },
      {
        id: 2,
        question: "When is surgery necessary?",
        answer: "Surgery is considered if severe pain persists despite conservative treatments or if there’s significant nerve compression causing weakness or loss of function."
      }
    ]
  };  

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Helmet>
        <title>{pageData.title} - NYC for Spinal Disorders</title>
        <meta name="description" content={pageData.metaDescription} />
        <meta name="keywords" content={pageData.metaKeywords} />
        <meta name="author" content="New York Center for Spinal Disorders" />
        <meta property="og:title" content={pageData.title} />
        <meta property="og:description" content={pageData.metaDescription} />
        <meta property="og:image" content="/images/logo-header.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      
      <Header />

      
      <div className="flex items-center relative min-h-[40vh] bg-gradient-to-r from-[#003666] to-[#0062B1] py-8">
        <div className="absolute inset-0">
          <img
            src="../images/circles.jpg"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative h-full max-w-6xl mx-auto px-4 flex items-center">
          <div>
            <h1 className="text-5xl font-bold text-white mb-4">{pageData.title}</h1>
            <p className="text-2xl text-blue-100">{pageData.heroDescription}</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-lg">
        <div className="max-w-6xl mx-auto py-6 px-4">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition">
              Schedule Consultation <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center px-6 py-3 border-2 border-blue-900 text-blue-900 rounded-full hover:bg-blue-50 transition">
              <Phone className="mr-2 w-4 h-4" /> Call Now
            </a>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <BookOpen className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Condition Overview</h2>
                {pageData.forWho.map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <Activity className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">What Patients Need to Know</h2>
                <ul className="space-y-4 text-gray-700">
                  {pageData.conditionInfo.map((info, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>{info}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>





        <div className="bg-gradient-to-r from-[#003666] to-[#0062B1] rounded-xl shadow-lg p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Patient-Centered Approach</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our dedicated team of board-certified orthopedic spine physicians at New York Center for Spinal Disorders works together to guide you every step of the way, always prioritizing your comfort and well-being.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 rounded-full font-bold hover:bg-blue-100 transition duration-300">
              Schedule Consultation
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white/10 transition duration-300">
              <Phone className="w-5 h-5 mr-2" />
              Call Now
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HerniatedDiscCondition;